import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface QueryLibrarySelectionState {
  selectedQueryIds: Set<string>;
  lastSelectedQueryId: string | null;
  
  // Actions
  toggleQuerySelection: (queryId: string, isCtrlPressed: boolean, isShiftPressed: boolean) => void;
  selectQuery: (queryId: string) => void;
  selectQueries: (queryIds: string[]) => void;
  deselectQuery: (queryId: string) => void;
  clearSelection: () => void;
  selectRange: (fromQueryId: string, toQueryId: string, allQueryIds: string[]) => void;
  isQuerySelected: (queryId: string) => boolean;
  getSelectedQueryIds: () => string[];
}

export const useQueryLibrarySelectionStore = create<QueryLibrarySelectionState>()(
  devtools(
    (set, get) => ({
      selectedQueryIds: new Set<string>(),
      lastSelectedQueryId: null,

      toggleQuerySelection: (queryId: string, isCtrlPressed: boolean, isShiftPressed: boolean) => {
        const state = get();
        const newSelectedIds = new Set(state.selectedQueryIds);
        
        if (isShiftPressed && state.lastSelectedQueryId) {
          // Shift selection is handled by selectRange method
          return;
        }
        
        if (isCtrlPressed) {
          // Ctrl/Cmd click - toggle selection
          if (newSelectedIds.has(queryId)) {
            newSelectedIds.delete(queryId);
          } else {
            newSelectedIds.add(queryId);
          }
        } else {
          // Regular click - select only this query
          newSelectedIds.clear();
          newSelectedIds.add(queryId);
        }
        
        set({ 
          selectedQueryIds: newSelectedIds,
          lastSelectedQueryId: queryId
        });
      },

      selectQuery: (queryId: string) => {
        set(state => ({
          selectedQueryIds: new Set(state.selectedQueryIds).add(queryId),
          lastSelectedQueryId: queryId
        }));
      },

      selectQueries: (queryIds: string[]) => {
        set({
          selectedQueryIds: new Set(queryIds),
          lastSelectedQueryId: queryIds.length > 0 ? queryIds[queryIds.length - 1] : null
        });
      },

      deselectQuery: (queryId: string) => {
        set(state => {
          const newSelectedIds = new Set(state.selectedQueryIds);
          newSelectedIds.delete(queryId);
          return {
            selectedQueryIds: newSelectedIds,
            lastSelectedQueryId: state.lastSelectedQueryId === queryId 
              ? Array.from(newSelectedIds).pop() || null 
              : state.lastSelectedQueryId
          };
        });
      },

      clearSelection: () => {
        set({
          selectedQueryIds: new Set(),
          lastSelectedQueryId: null
        });
      },

      selectRange: (fromQueryId: string, toQueryId: string, allQueryIds: string[]) => {
        console.log('selectRange called:', { fromQueryId, toQueryId, allQueryIds });
        
        const fromIndex = allQueryIds.indexOf(fromQueryId);
        const toIndex = allQueryIds.indexOf(toQueryId);
        
        console.log('Indices:', { fromIndex, toIndex });
        
        if (fromIndex === -1 || toIndex === -1) {
          console.error('Query IDs not found in allQueryIds!', {
            fromQueryId,
            toQueryId,
            allQueryIds,
            fromFound: fromIndex !== -1,
            toFound: toIndex !== -1
          });
          return;
        }
        
        const startIndex = Math.min(fromIndex, toIndex);
        const endIndex = Math.max(fromIndex, toIndex);
        
        const rangeQueryIds = allQueryIds.slice(startIndex, endIndex + 1);
        console.log('Range query IDs:', rangeQueryIds);
        
        set(state => ({
          selectedQueryIds: new Set([...state.selectedQueryIds, ...rangeQueryIds]),
          lastSelectedQueryId: toQueryId
        }));
      },

      isQuerySelected: (queryId: string) => {
        return get().selectedQueryIds.has(queryId);
      },

      getSelectedQueryIds: () => {
        return Array.from(get().selectedQueryIds);
      }
    }),
    {
      name: 'query-library-selection'
    }
  )
);